import React from "react";
import SafeIcon from "../../../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const toDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function getRange(preset) {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const quarterStart = Math.floor(month / 3) * 3;
  const lastQuarterDate = new Date(year, quarterStart, 0);
  const ranges = {
    thisMonth: [new Date(year, month, 1), today],
    lastMonth: [new Date(year, month - 1, 1), new Date(year, month, 0)],
    thisQuarter: [new Date(year, quarterStart, 1), today],
    lastQuarter: [
      new Date(year, quarterStart - 3, 1),
      lastQuarterDate,
    ],
    thisYear: [new Date(year, 0, 1), today],
    lastYear: [new Date(year - 1, 0, 1), new Date(year - 1, 11, 31)],
  };
  return ranges[preset]?.map(toDate);
}

export default function ClientReportFilters({
  reportType,
  dates,
  accounts,
  accountId,
  page,
  search,
  loading,
  onDatesChange,
  onAccountChange,
  onPageChange,
  onSearchChange,
  onGenerate,
}) {
  const asOfOnly = ["balanceSheet", "trialBalance"].includes(reportType);
  const needsPeriod = ["profitLoss", "cashFlow", "generalLedger"].includes(reportType);
  const supported = reportType !== "cashFlow";

  const applyPreset = (preset) => {
    const range = getRange(preset);
    if (range) onDatesChange({ startDate: range[0], endDate: range[1] });
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-wrap items-end gap-3">
          {needsPeriod && (
            <>
              <label className="grid gap-1 text-xs font-semibold text-slate-500">
                Period
                <select
                  defaultValue="custom"
                  onChange={(event) => applyPreset(event.target.value)}
                  className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-violet-400"
                >
                  <option value="custom">Custom range</option>
                  <option value="thisMonth">This Month</option>
                  <option value="lastMonth">Last Month</option>
                  <option value="thisQuarter">This Quarter</option>
                  <option value="lastQuarter">Last Quarter</option>
                  <option value="thisYear">This Year</option>
                  <option value="lastYear">Last Year</option>
                </select>
              </label>
              <label className="grid gap-1 text-xs font-semibold text-slate-500">
                From
                <input type="date" value={dates.startDate} onChange={(event) => onDatesChange({ ...dates, startDate: event.target.value })} className="h-10 rounded-xl border border-slate-200 px-3 text-sm text-slate-700" />
              </label>
              <label className="grid gap-1 text-xs font-semibold text-slate-500">
                To
                <input type="date" value={dates.endDate} onChange={(event) => onDatesChange({ ...dates, endDate: event.target.value })} className="h-10 rounded-xl border border-slate-200 px-3 text-sm text-slate-700" />
              </label>
            </>
          )}
          {asOfOnly && (
            <label className="grid gap-1 text-xs font-semibold text-slate-500">
              As of date
              <input type="date" value={dates.endDate} onChange={(event) => onDatesChange({ ...dates, endDate: event.target.value })} className="h-10 rounded-xl border border-slate-200 px-3 text-sm text-slate-700" />
            </label>
          )}
          {reportType === "generalLedger" && (
            <>
              <label className="grid gap-1 text-xs font-semibold text-slate-500">
                Account
                <select value={accountId} onChange={(event) => onAccountChange(event.target.value)} className="h-10 min-w-48 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700">
                  <option value="">Select an account</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {[account.code, account.name].filter(Boolean).join(" · ")}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1 text-xs font-semibold text-slate-500">
                Search
                <input type="search" value={search} onChange={(event) => onSearchChange(event.target.value)} placeholder="Description or reference" className="h-10 rounded-xl border border-slate-200 px-3 text-sm text-slate-700" />
              </label>
              <label className="grid gap-1 text-xs font-semibold text-slate-500">
                Page
                <input type="number" min="1" value={page} onChange={(event) => onPageChange(Math.max(1, Number(event.target.value) || 1))} className="h-10 w-24 rounded-xl border border-slate-200 px-3 text-sm text-slate-700" />
              </label>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={onGenerate}
          disabled={loading || !supported}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 text-sm font-bold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <SafeIcon icon={loading ? FiIcons.FiLoader : FiIcons.FiPlay} className={loading ? "animate-spin" : ""} />
          {loading ? "Generating..." : "Generate Report"}
        </button>
      </div>
      {!supported && (
        <p className="mt-3 text-xs text-amber-700">
          Cash Flow is unavailable because the existing calculation reads legacy tables rather than the active organization ledger.
        </p>
      )}
    </section>
  );
}